import type { VariantProps } from 'class-variance-authority';
import * as React from 'react';
import IconButton from '#components/icon-button/icon-button.tsx';
import { cn } from '#lib/utils.ts';
import { cva } from 'class-variance-authority';
import { CheckCircle2, CircleAlert, Info, TriangleAlert, X } from 'lucide-react';

const bannerVariants = cva('relative w-full rounded-lg border py-3 px-4 text-sm', {
  variants: {
    variant: {
      default: 'bg-card text-card-foreground border-border-light [&_[data-slot="alert-description"]]:text-secondary',
      destructive: 'text-destructive-foreground-dark bg-destructive-background border-destructive-outline',
      warning: 'bg-warning-background text-warning-foreground-dark border-warning-outline',
      info: 'bg-info-background text-info-foreground-dark border-info-outline',
      success: 'bg-success-background text-success-foreground-dark border-success-outline',
    },
  },
  defaultVariants: {
    variant: 'default',
  },
});

type BannerVariant = NonNullable<VariantProps<typeof bannerVariants>['variant']>;

const DEFAULT_VARIANT_ICONS: Partial<Record<BannerVariant, React.ReactNode>> = {
  destructive: <CircleAlert />,
  warning: <TriangleAlert />,
  info: <Info />,
  success: <CheckCircle2 />,
};

type BannerProps = React.ComponentProps<'div'> &
  VariantProps<typeof bannerVariants> & {
    readonly title?: string;
    readonly description?: React.ReactNode;
    readonly handleClickX?: () => void;
    readonly icon?: React.ReactNode;
    readonly hideIcon?: boolean;
    readonly actionButton?: React.ReactNode;
  };

function Banner({
  className,
  variant,
  title,
  description,
  handleClickX,
  icon,
  hideIcon = false,
  actionButton,
  ...props
}: BannerProps) {
  const resolvedIcon = hideIcon || icon === null ? undefined : (icon ?? DEFAULT_VARIANT_ICONS[variant ?? 'default']);
  return (
    <div data-slot="alert" role="alert" className={cn(bannerVariants({ variant }), className)} {...props}>
      <div className="flex flex-row justify-between items-center w-full gap-3">
        <div className="flex flex-row gap-3">
          {resolvedIcon ? (
            <div aria-hidden="true" className="[&_svg]:size-4 my-0.5">
              {resolvedIcon}
            </div>
          ) : null}
          <div className="flex flex-col gap-1 w-full">
            {title && (
              <div data-slot="alert-title" className="font-medium line-clamp-1">
                {title}
              </div>
            )}
            {description && <div data-slot="alert-description">{description}</div>}
          </div>
        </div>
        <div className="flex flex-row items-center gap-3">
          {actionButton && actionButton}
          {handleClickX && (
            <IconButton
              variant="ghost"
              size="xs"
              onClick={handleClickX}
              className="hover:bg-transparent text-dark"
              aria-label="Dismiss"
            >
              <X />
            </IconButton>
          )}
        </div>
      </div>
    </div>
  );
}

export default Banner;
